<template>
  <div class="upload-container">
    <!-- 导航栏 -->
    <nav class="navbar">
      <div class="nav-content">
        <h1 class="nav-title">🎬 视频上传</h1>
        <div class="nav-actions">
          <router-link to="/dashboard" class="nav-link">返回面板</router-link>
          <button @click="handleLogout" class="logout-btn">退出登录</button>
        </div>
      </div>
    </nav>

    <!-- 主要内容 -->
    <main class="main-content">
      <div class="content-wrapper">
        <!-- 上传表单 -->
        <div class="upload-card">
          <div class="card-header">
            <h2>📤 上传视频到TikTok</h2>
            <p class="card-subtitle">支持 MP4、MOV、AVI 等格式，最大文件大小 1GB</p>
          </div>

          <form @submit.prevent="handleUpload" class="upload-form">
            <!-- 文件选择区域 -->
            <div class="form-group file-upload-area">
              <label class="file-label">
                <input
                  type="file"
                  ref="fileInput"
                  @change="handleFileSelect"
                  accept="video/*"
                  :disabled="state.uploading"
                  class="file-input"
                >
                <div class="file-drop-zone" :class="{ 'dragover': state.isDragOver }">
                  <div v-if="!state.selectedFile" class="drop-zone-content">
                    <div class="upload-icon">📁</div>
                    <p class="upload-text">点击选择视频文件或拖拽到此处</p>
                    <p class="upload-hint">支持 MP4、MOV、AVI 等格式</p>
                  </div>
                  <div v-else class="file-preview">
                    <div class="file-info">
                      <div class="file-icon">🎬</div>
                      <div class="file-details">
                        <p class="file-name">{{ state.selectedFile.name }}</p>
                        <p class="file-size">{{ formatFileSize(state.selectedFile.size) }}</p>
                      </div>
                      <button 
                        type="button" 
                        @click="removeFile" 
                        :disabled="state.uploading"
                        class="remove-file-btn"
                      >
                        ❌
                      </button>
                    </div>
                  </div>
                </div>
              </label>
            </div>

            <!-- 视频标题 -->
            <div class="form-group">
              <label for="title" class="form-label">视频标题 *</label>
              <input
                id="title"
                v-model="form.title"
                type="text"
                class="form-input"
                placeholder="请输入视频标题"
                maxlength="150"
                :disabled="state.uploading"
                required
              >
              <p class="input-hint">{{ form.title.length }}/150 字符</p>
            </div>

            <!-- 隐私设置 -->
            <div class="form-group">
              <label for="privacyLevel" class="form-label">隐私级别</label>
              <select 
                id="privacyLevel"
                v-model="form.privacyLevel" 
                class="form-select"
                :disabled="state.uploading"
              >
                <option value="PUBLIC_TO_EVERYONE">所有人可见</option>
                <option value="MUTUAL_FOLLOW_FRIENDS">互关朋友可见</option>
                <option value="FOLLOWER_OF_CREATOR">粉丝可见</option>
                <option value="SELF_ONLY">仅自己可见</option>
              </select>
            </div>

            <!-- 互动设置 -->
            <div class="form-group">
              <label class="form-label">互动设置</label>
              <div class="checkbox-group">
                <label class="checkbox-item">
                  <input
                    v-model="form.disableDuet"
                    type="checkbox"
                    :disabled="state.uploading"
                    class="checkbox-input"
                  >
                  <span class="checkbox-label">禁用合拍</span>
                </label>
                <label class="checkbox-item">
                  <input
                    v-model="form.disableComment"
                    type="checkbox"
                    :disabled="state.uploading"
                    class="checkbox-input"
                  >
                  <span class="checkbox-label">禁用评论</span>
                </label>
                <label class="checkbox-item">
                  <input
                    v-model="form.disableStitch"
                    type="checkbox"
                    :disabled="state.uploading"
                    class="checkbox-input"
                  >
                  <span class="checkbox-label">禁用缝合</span>
                </label>
              </div>
            </div>

            <!-- 高级设置 -->
            <div class="form-group advanced-settings" v-show="state.showAdvanced">
              <label for="videoCoverTimestamp" class="form-label">视频封面时间戳 (毫秒)</label>
              <input
                id="videoCoverTimestamp"
                v-model.number="form.videoCoverTimestampMs"
                type="number"
                class="form-input"
                min="0"
                :disabled="state.uploading"
                placeholder="1000"
              >
              
              <label for="chunkSize" class="form-label">分块大小 (MB)</label>
              <input
                id="chunkSize"
                v-model.number="form.chunkSizeMB"
                type="number"
                class="form-input"
                min="1"
                max="100"
                :disabled="state.uploading"
                placeholder="10"
              >
            </div>

            <button 
              type="button"
              @click="state.showAdvanced = !state.showAdvanced"
              class="advanced-toggle"
              :disabled="state.uploading"
            >
              {{ state.showAdvanced ? '隐藏' : '显示' }}高级设置
            </button>

            <!-- 上传进度 -->
            <div v-if="state.uploading" class="upload-progress">
              <div class="progress-header">
                <span class="progress-text">上传中...</span>
                <span class="progress-percent">{{ state.uploadProgress }}%</span>
              </div>
              <div class="progress-bar">
                <div 
                  class="progress-fill" 
                  :style="{ width: state.uploadProgress + '%' }"
                ></div>
              </div>
            </div>

            <!-- 操作按钮 -->
            <div class="form-actions">
              <button
                type="submit"
                :disabled="!canUpload"
                class="btn btn-primary"
              >
                <span v-if="state.uploading">⏳</span>
                <span v-else>🚀</span>
                {{ state.uploading ? '上传中...' : '开始上传' }}
              </button>
              
              <button
                type="button"
                @click="resetForm"
                :disabled="state.uploading"
                class="btn btn-outline"
              >
                重置
              </button>
            </div>
          </form>
        </div>

        <!-- 上传结果 -->
        <div v-if="state.uploadResult" class="result-card" :class="state.uploadResult.success ? 'success' : 'error'">
          <div class="result-header">
            <span class="result-icon">{{ state.uploadResult.success ? '✅' : '❌' }}</span>
            <h3 class="result-title">{{ state.uploadResult.success ? '上传成功' : '上传失败' }}</h3>
          </div>
          <div class="result-content">
            <pre class="result-data">{{ JSON.stringify(state.uploadResult, null, 2) }}</pre>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { TikTokAPI, TokenManager } from '../api/tiktok'

export default {
  name: 'Upload',
  setup() {
    const router = useRouter()

    // 检查登录状态
    onMounted(() => {
      if (!TokenManager.isLoggedIn()) {
        router.push('/login')
        return
      }
    })

    // 响应式状态
    const state = reactive({
      selectedFile: null,
      uploading: false,
      uploadProgress: 0,
      uploadResult: null,
      isDragOver: false,
      showAdvanced: false
    })

    // 表单数据
    const form = reactive({
      title: '',
      privacyLevel: 'PUBLIC_TO_EVERYONE',
      disableDuet: false,
      disableComment: false,
      disableStitch: false,
      videoCoverTimestampMs: 1000,
      chunkSizeMB: 10
    })

    // 计算属性
    const canUpload = computed(() => {
      return state.selectedFile && form.title.trim() && !state.uploading
    })

    // 方法
    const handleFileSelect = (event) => {
      const file = event.target.files[0]
      if (file) {
        state.selectedFile = file
        // 如果标题为空，使用文件名作为默认标题
        if (!form.title.trim()) {
          form.title = file.name.replace(/\.[^/.]+$/, '')
        }
      }
    }

    const removeFile = () => {
      state.selectedFile = null
      const fileInput = document.querySelector('.file-input')
      if (fileInput) {
        fileInput.value = ''
      }
    }

    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const handleUpload = async () => {
      if (!canUpload.value) return

      try {
        state.uploading = true
        state.uploadProgress = 0
        state.uploadResult = null

        const uploadData = {
          title: form.title.trim(),
          videoFile: state.selectedFile,
          privacyLevel: form.privacyLevel,
          disableDuet: form.disableDuet,
          disableComment: form.disableComment,
          disableStitch: form.disableStitch,
          videoCoverTimestampMs: form.videoCoverTimestampMs,
          chunkSize: form.chunkSizeMB * 1024 * 1024 // 转换为字节
        }

        const result = await TikTokAPI.uploadVideo(uploadData, (progress) => {
          state.uploadProgress = progress
        })

        state.uploadResult = result

        if (result.success) {
          // 上传成功后重置表单
          setTimeout(() => {
            resetForm()
          }, 3000)
        }

      } catch (error) {
        console.error('上传过程中发生错误:', error)
        state.uploadResult = {
          success: false,
          error: error.message || '上传过程中发生未知错误'
        }
      } finally {
        state.uploading = false
      }
    }

    const resetForm = () => {
      // 重置表单
      form.title = ''
      form.privacyLevel = 'PUBLIC_TO_EVERYONE'
      form.disableDuet = false
      form.disableComment = false
      form.disableStitch = false
      form.videoCoverTimestampMs = 1000
      form.chunkSizeMB = 10

      // 重置状态
      state.selectedFile = null
      state.uploadProgress = 0
      state.uploadResult = null
      state.showAdvanced = false

      // 重置文件输入
      const fileInput = document.querySelector('.file-input')
      if (fileInput) {
        fileInput.value = ''
      }
    }

    const handleLogout = () => {
      TokenManager.clearTokens()
      router.push('/login')
    }

    return {
      state,
      form,
      canUpload,
      handleFileSelect,
      removeFile,
      formatFileSize,
      handleUpload,
      resetForm,
      handleLogout
    }
  }
}
</script>

<style scoped>
.upload-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #333;
}

.navbar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1rem 0;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
}

.nav-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.nav-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #4F46E5;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.nav-link {
  color: #6B7280;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
}

.nav-link:hover {
  color: #4F46E5;
}

.logout-btn {
  background: #EF4444;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.logout-btn:hover {
  background: #DC2626;
  transform: translateY(-1px);
}

.main-content {
  padding: 2rem 0;
}

.content-wrapper {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
}

.upload-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

.card-header {
  text-align: center;
  margin-bottom: 2rem;
}

.card-header h2 {
  margin: 0 0 0.5rem 0;
  color: #1F2937;
  font-size: 1.75rem;
  font-weight: 700;
}

.card-subtitle {
  color: #6B7280;
  margin: 0;
}

.upload-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}

.file-upload-area {
  margin-bottom: 1rem;
}

.file-label {
  cursor: pointer;
}

.file-input {
  display: none;
}

.file-drop-zone {
  border: 2px dashed #D1D5DB;
  border-radius: 0.75rem;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  background: #F9FAFB;
}

.file-drop-zone:hover,
.file-drop-zone.dragover {
  border-color: #4F46E5;
  background: #EEF2FF;
}

.drop-zone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.upload-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
}

.upload-text {
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.upload-hint {
  color: #6B7280;
  font-size: 0.875rem;
  margin: 0;
}

.file-preview {
  padding: 0.5rem;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.file-icon {
  font-size: 2rem;
}

.file-details {
  flex: 1;
}

.file-name {
  font-weight: 600;
  color: #1F2937;
  margin: 0;
}

.file-size {
  color: #6B7280;
  font-size: 0.875rem;
  margin: 0.25rem 0 0 0;
}

.remove-file-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.25rem;
  transition: background 0.3s ease;
}

.remove-file-btn:hover {
  background: #FEE2E2;
}

.form-input,
.form-select {
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #4F46E5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.input-hint {
  color: #6B7280;
  font-size: 0.75rem;
  margin: 0;
  text-align: right;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.checkbox-input {
  width: 1rem;
  height: 1rem;
}

.checkbox-label {
  color: #374151;
  font-size: 0.9rem;
}

.advanced-settings {
  background: #F9FAFB;
  padding: 1.5rem;
  border-radius: 0.5rem;
  border: 1px solid #E5E7EB;
}

.advanced-toggle {
  background: none;
  border: 1px solid #D1D5DB;
  color: #4F46E5;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  align-self: flex-start;
}

.advanced-toggle:hover {
  background: #EEF2FF;
}

.upload-progress {
  margin: 1rem 0;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.progress-text {
  color: #374151;
  font-weight: 500;
}

.progress-percent {
  color: #4F46E5;
  font-weight: 600;
}

.progress-bar {
  height: 0.5rem;
  background: #E5E7EB;
  border-radius: 0.25rem;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4F46E5, #7C3AED);
  transition: width 0.3s ease;
}

.form-actions {
  display: flex;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #E5E7EB;
}

.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background: linear-gradient(135deg, #4F46E5, #7C3AED);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(79, 70, 229, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-outline {
  background: white;
  border: 2px solid #D1D5DB;
  color: #374151;
}

.btn-outline:hover:not(:disabled) {
  border-color: #4F46E5;
  color: #4F46E5;
}

.result-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}

.result-card.success {
  border-left: 4px solid #10B981;
}

.result-card.error {
  border-left: 4px solid #EF4444;
}

.result-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.result-icon {
  font-size: 1.5rem;
}

.result-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.result-card.success .result-title {
  color: #059669;
}

.result-card.error .result-title {
  color: #DC2626;
}

.result-data {
  background: #F3F4F6;
  border-radius: 0.5rem;
  padding: 1rem;
  overflow-x: auto;
  font-size: 0.875rem;
  color: #374151;
  margin: 0;
  white-space: pre-wrap;
}

@media (max-width: 768px) {
  .content-wrapper {
    padding: 0 1rem;
  }
  
  .upload-card {
    padding: 1.5rem;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .nav-content {
    padding: 0 1rem;
  }
  
  .nav-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style> 